import { useState } from 'react';
import { useIntl } from 'react-intl';
import { useLoaderData, useRevalidator } from 'react-router-dom';
import {
  EmptyState,
  InfoBox,
  StatusBadge,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Button,
  cn,
  type StatusBadgeTone,
} from '@immo/ui-kit';
import type { FleetRegisteredAccountState, FleetRegisteredAccountView } from '@immo/api-contract';
import { apiClient } from '@/lib/auth/api-client';
import { useCan } from '@/lib/auth/permissions';
import { useTranslations } from '@/i18n/use-translations';
import { KeyIcon } from '@/components/layout/icons';
import { ListLeading } from '@/components/layout/page-leading';
import { PageTopbar } from '@/components/layout/topbar-slots';
import { MaxWeeklyDialog } from '@/components/fleet/max-weekly-dialog';

export type FleetAccountsLoaderData = { readonly accounts: ReadonlyArray<FleetRegisteredAccountView> };

export async function fleetAccountsLoader(): Promise<FleetAccountsLoaderData> {
  const result = await apiClient.adminFleetAccounts.list();

  if (!result.ok) throw new Response('Failed to load fleet accounts', { status: result.status });

  return { accounts: result.body.data };
}

const STATE_TONE: Record<FleetRegisteredAccountState, StatusBadgeTone> = {
  reauth: 'danger',
  paused: 'neutral',
  limited: 'warning',
  ready: 'success',
};

function percent(fraction: number): string {
  return `${Math.round(fraction * 100)} %`;
}

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/**
 * A reset as the operator reads it: a countdown while it is under a day away, the weekday and time beyond that;
 * `null` once it has passed — the reading predates the reset and the next tick brings a fresh one.
 */
function useResetLabel() {
  const t = useTranslations('pages.admin.fleet-accounts');
  const intl = useIntl();

  const duration = (ms: number): string => {
    if (ms >= DAY)
      return t('duration.days-hours', { days: Math.floor(ms / DAY), hours: Math.floor((ms % DAY) / HOUR) });

    if (ms >= HOUR)
      return t('duration.hours-minutes', { hours: Math.floor(ms / HOUR), minutes: Math.floor((ms % HOUR) / MINUTE) });

    return t('duration.minutes', { minutes: Math.max(1, Math.ceil(ms / MINUTE)) });
  };

  const at = (iso: string): string => intl.formatDate(iso, { weekday: 'short', hour: '2-digit', minute: '2-digit' });

  return {
    /** "Reset in 2 Std. 10 Min." under a day away, "Reset Mo., 09:00" beyond. */
    reset: (iso: string | null): string | null => {
      if (iso === null) return null;
      const left = new Date(iso).getTime() - Date.now();

      if (left <= 0) return null;

      return left < DAY ? t('resets-in', { duration: duration(left) }) : t('resets-at', { time: at(iso) });
    },
    /** "zurück Mo., 09:00 · in 2 T. 18 Std." — both, since a limited account is the one the operator waits on. */
    back: (iso: string): string => {
      const left = new Date(iso).getTime() - Date.now();

      return left <= 0 ? t('back-at', { time: at(iso) }) : t('back-at-in', { time: at(iso), duration: duration(left) });
    },
  };
}

/**
 * Fleet → Accounts (fleet-agent ADR 0027): every engine account a worker holds,
 * which machine holds it, its last reading and the operator's settings. Logins
 * live on the workers — re-auth and disconnect happen there, not here.
 */
export function FleetAccountsPage() {
  const t = useTranslations('pages.admin.fleet-accounts');
  const { accounts } = useLoaderData() as FleetAccountsLoaderData;
  const { revalidate } = useRevalidator();
  const canCommand = useCan('backoffice:fleet:command');
  const [editing, setEditing] = useState<FleetRegisteredAccountView | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rowError, setRowError] = useState(false);

  /** One settings write; in the dialog a failure keeps it open with the error, on a row it shows above the table. */
  const update = async (
    account: FleetRegisteredAccountView,
    body: { readonly enabled?: boolean; readonly maxWeekly?: number },
    inDialog: boolean,
  ) => {
    setBusy(true);
    setError(null);
    setRowError(false);

    try {
      const result = await apiClient.adminFleetAccounts.update({ pathParams: { accountId: account.id }, body });

      if (!result.ok) {
        if (inDialog) setError(t('save-error'));
        else setRowError(true);

        return;
      }

      if (inDialog) setEditing(null);
      revalidate();
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PageTopbar leading={<ListLeading title={t('title')} count={accounts.length} icon={KeyIcon} />} />

      <div className="space-y-3 p-4">
        <p className="text-sm text-text-secondary">{t('subtitle')}</p>
        {!canCommand && <InfoBox type="info">{t('read-only-hint')}</InfoBox>}
        {rowError && <InfoBox type="danger">{t('save-error')}</InfoBox>}
      </div>

      {accounts.length === 0 ? (
        <div className="p-6">
          <EmptyState title={t('empty')} />
        </div>
      ) : (
        <Table flush>
          <TableHeader>
            <TableRow>
              <TableHead>{t('columns.account')}</TableHead>
              <TableHead>{t('columns.machine')}</TableHead>
              <TableHead className="w-[180px]">{t('columns.session')}</TableHead>
              <TableHead className="w-[180px]">{t('columns.week')}</TableHead>
              <TableHead className="w-[210px]">{t('columns.state')}</TableHead>
              <TableHead className="w-[80px]">{t('columns.enabled')}</TableHead>
              {canCommand && <TableHead>{t('columns.actions')}</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {accounts.map((account) => (
              <TableRow key={account.id}>
                <TableCell>
                  <div className="flex min-w-0 flex-col">
                    <span className="truncate font-medium text-text-primary">{account.email}</span>
                    <span className="truncate text-sm text-text-tertiary">
                      {[account.engine, ...account.lanes].join(' · ')}
                    </span>
                  </div>
                </TableCell>
                <TableCell>
                  <span className="text-text-primary">{account.machine.name}</span>
                </TableCell>
                <TableCell>
                  <UsageCell
                    label={t('columns.session')}
                    value={account.quota?.session ?? null}
                    cap={1}
                    resetsAt={account.quota?.sessionResetsAt ?? null}
                  />
                </TableCell>
                <TableCell>
                  <UsageCell
                    label={t('columns.week')}
                    value={account.quota?.week ?? null}
                    cap={account.maxWeekly}
                    resetsAt={account.quota?.weekResetsAt ?? null}
                  />
                </TableCell>
                <TableCell>
                  <AccountState account={account} />
                </TableCell>
                <TableCell>
                  <Switch
                    checked={account.enabled}
                    label={t('enabled-label', { label: account.email })}
                    disabled={!canCommand || busy}
                    onCheckedChange={(enabled) => void update(account, { enabled }, false)}
                  />
                </TableCell>
                {canCommand && (
                  <TableCell>
                    <Button size="sm" variant="ghost" type="button" onClick={() => setEditing(account)}>
                      {account.maxWeekly < 1
                        ? t('limit-cta.capped', { percent: Math.round(account.maxWeekly * 100) })
                        : t('limit-cta.uncapped')}
                    </Button>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {editing !== null && (
        <MaxWeeklyDialog
          accountLabel={editing.email}
          current={editing.maxWeekly}
          busy={busy}
          error={error}
          onClose={() => {
            setEditing(null);
            setError(null);
          }}
          onSave={(maxWeekly) => void update(editing, { maxWeekly }, true)}
        />
      )}
    </>
  );
}

/**
 * A usage window as a bar: the fill is the reading, red once it reaches the cap; a cap below 100 % is named, and
 * when the holder reports it, when the window refills.
 */
function UsageCell({
  label,
  value,
  cap,
  resetsAt,
}: {
  readonly label: string;
  readonly value: number | null;
  readonly cap: number;
  readonly resetsAt: string | null;
}) {
  const t = useTranslations('pages.admin.fleet-accounts');
  const reset = useResetLabel().reset(resetsAt);

  if (value === null) return <span className="text-sm text-text-tertiary">{t('no-usage')}</span>;

  const atCap = value >= cap;

  return (
    <div className="flex flex-col gap-1">
      <div
        role="meter"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(value * 100)}
        className="relative h-1.5 w-full overflow-hidden rounded-pill bg-subtle"
      >
        <div
          className={cn('h-full rounded-pill', atCap ? 'bg-danger-500' : 'bg-interactive')}
          style={{ width: `${Math.min(100, Math.round(value * 100))}%` }}
        />
        {cap < 1 && (
          <div
            aria-hidden
            className="absolute inset-y-0 w-px bg-neutral-500"
            style={{ left: `${Math.round(cap * 100)}%` }}
          />
        )}
      </div>
      <span className={cn('font-mono text-sm tabular-nums', atCap ? 'text-danger-600' : 'text-text-tertiary')}>
        {percent(value)}
        {cap < 1 && ` · ${t('max-weekly', { percent: percent(cap) })}`}
      </span>
      {reset !== null && <span className="text-sm text-text-tertiary">{reset}</span>}
    </div>
  );
}

/**
 * When a limited account takes work again. The holder's own `limitedUntil` (a spent session or week, the engine's
 * refusal, a 429) when it sends one, the later of it and the week's reset while the operator's cap is reached; an
 * older agent sends neither, and the week's reset is all there is.
 */
function limitedUntil(account: FleetRegisteredAccountView): string | null {
  const weekResetsAt = account.quota?.weekResetsAt ?? null;

  if (account.limitedUntil === undefined) return weekResetsAt;

  const capped = account.quota !== null && (account.quota.week ?? 0) >= account.maxWeekly ? weekResetsAt : null;
  const held = account.limitedUntil;

  if (held === null || capped === null) return held ?? capped;

  return new Date(held) > new Date(capped) ? held : capped;
}

/**
 * Which window holds a limited account: the week once it reaches the operator's cap or runs out, else the session
 * once it runs out; neither when the engine refused on its own (a 429) with both windows open.
 */
function limitingWindow(account: FleetRegisteredAccountView): 'session' | 'week' | null {
  const quota = account.quota;

  if (quota === null) return null;

  if (quota.week !== null && quota.week >= Math.min(1, account.maxWeekly)) return 'week';

  if (quota.session !== null && quota.session >= 1) return 'session';

  return null;
}

/** The backend's state as a badge naming the window that holds it, when it is back, how to fix a lost login, and when the reading was taken. */
function AccountState({ account }: { readonly account: FleetRegisteredAccountView }) {
  const t = useTranslations('pages.admin.fleet-accounts');
  const intl = useIntl();
  const { back } = useResetLabel();
  const quota = account.quota;
  const limited = account.state === 'limited';
  const resetsAt = limited ? limitedUntil(account) : null;
  const held = limited ? limitingWindow(account) : null;
  const label = held !== null ? t(`state.limited-${held}`) : t(`state.${account.state}`);

  return (
    <div className="flex flex-col gap-1">
      <StatusBadge label={label} tone={STATE_TONE[account.state]} dot />
      {resetsAt !== null && <span className="text-sm text-text-secondary">{back(resetsAt)}</span>}
      {account.state === 'reauth' && <span className="text-sm text-text-tertiary">{t('reauth-hint')}</span>}
      {quota !== null && (
        <span className="text-sm text-text-tertiary">
          {t('usage-read-at', {
            time: intl.formatDate(quota.readAt, {
              day: '2-digit',
              month: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
            }),
          })}
        </span>
      )}
    </div>
  );
}
